/**
 * useNotifications Composable
 *
 * Purpose:
 * This composable provides functionality to manage and display notifications. It allows for the creation of notifications with unique IDs,
 * and the automatic removal of these notifications after a specified timeout duration.
 *
 * Returns:
 * - notifications: A ref holding an array of current notifications.
 * - notify: A function to add a new notification with an optional timeout duration (default is 3000ms).
 * - removeNotification: A function to manually remove a notification by its unique ID.
 *
 * Usage Example:
 * ```javascript
 * const { notifications, notify, removeNotification } = useNotifications();
 * notify({ message: 'This is a notification!' }, 5000);
 * ```
 *
 * Internal Functions:
 * - generateId: Generates a unique ID for each new notification.
 */
import { ref } from 'vue';

let instance = null;

export default function useNotifications() {
  if (instance) return instance;

  const notifications = ref([]);

  let count = 0;

  const generateId = () => {
    count = count + 1;
    return count;
  };

  const notify = (notification, timeout = 3000) => {
    notification.id = generateId();
    notifications.value.push(notification);
    setTimeout(() => {
      removeNotification(notification.id);
    }, timeout);
  };

  const removeNotification = (id) => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index !== -1) {
      notifications.value.splice(index, 1);
    }
  };

  instance = {
    notifications,
    notify,
    removeNotification,
  };

  return instance;
}
